import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../../../../../store/store';
import SidePopup from '../../../../../shared/SidePopup/SidePopup';
import DeliveryAddressPopupContent from '../../../AccountPage/components/new/popups/DeliveryAddressPopupContent';
import type { UserProfile } from '../../../AccountPage/components/new/popups/userData.types';
import { isRecord } from '../../../AccountPage/components/new/popups/userData.types';
import type { AddressStruct } from '../../../../../../types/common';
import './deliverySection.scss';

type FormValue = Record<string, string>;
type FormErrors = Record<string, string | undefined>;
type ShippingMethod = 'pickup' | 'dpd';
type SelectedDeliveryAddress = {
    raw: string;
    struct: AddressStruct | null;
    comment?: string;
} | null;
type Props = {
    authUser: unknown;
    isAuthenticated: boolean;
    value: FormValue;
    errors: FormErrors;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    shippingMethod: ShippingMethod;
    setShippingMethod: Dispatch<SetStateAction<ShippingMethod>>;
    shippingCost: number | null;
    shippingError: string;
    shippingStatusText: string;
    isFreeDeliveryEligible: boolean;
    isFreeDeliveryZone: boolean;
    freeDeliveryMinTotal: number;
    goodsPayable: number;
    onSelectedAddressChange?: (address: SelectedDeliveryAddress) => void;
    highlightPickupNote?: boolean;
};
type AddressOption = {
    key: string; // 'primary' | `additional:${id}`
    kind: 'primary' | 'additional';
    id?: string;
    raw: string;
    struct: AddressStruct | null;
    comment: string;
};

function toTrimmedString(v: unknown): string {
    return typeof v === 'string' ? v.trim() : '';
}

function normalizeRawKey(raw: string): string {
    return raw.trim().replace(/\s+/g, ' ').toLowerCase();
}

function readPrimaryAddress(user: unknown): { raw: string; struct: AddressStruct | null; comment: string } | null {
    if (!user || typeof user !== 'object') return null;

    const addr = (user as Record<string, unknown>).address;

    if (typeof addr === 'string') {
        const raw = addr.trim();
        if (!raw) return null;
        return { raw, struct: null, comment: '' };
    }

    if (!addr || typeof addr !== 'object') return null;

    const raw = toTrimmedString((addr as Record<string, unknown>).raw);
    if (!raw) return null;

    const structUnknown = (addr as Record<string, unknown>).struct;
    const struct = isRecord(structUnknown) ? (structUnknown as unknown as AddressStruct) : null;

    const comment = toTrimmedString((addr as Record<string, unknown>).comment);

    return { raw, struct, comment };
}

function readAdditionalAddresses(user: unknown): Array<{ id: string; raw: string; struct: AddressStruct | null; comment: string; isDisabled: boolean }> {
    if (!user || typeof user !== 'object') return [];

    const listUnknown = (user as Record<string, unknown>).addresses;
    if (!Array.isArray(listUnknown)) return [];

    const out: Array<{ id: string; raw: string; struct: AddressStruct | null; comment: string; isDisabled: boolean }> = [];

    for (let i = 0; i < listUnknown.length; i += 1) {
        const item = listUnknown[i];
        if (!item || typeof item !== 'object') continue;

        const raw = toTrimmedString((item as Record<string, unknown>).raw);
        if (!raw) continue;

        const isDisabled = Boolean((item as Record<string, unknown>).isDisabled);

        const idRaw = (item as Record<string, unknown>)._id ?? (item as Record<string, unknown>).id ?? String(i);
        const id = String(idRaw);

        const structUnknown = (item as Record<string, unknown>).struct;
        const struct = isRecord(structUnknown) ? (structUnknown as unknown as AddressStruct) : null;

        const comment = toTrimmedString((item as Record<string, unknown>).comment);

        out.push({ id, raw, struct, comment, isDisabled });
    }

    return out;
}

function buildTargetForPopup(opt: AddressOption): { id: string; raw: string; struct?: unknown | null; comment?: string; isPrimary: boolean } {
    return {
        id: opt.kind === 'primary' ? 'primary' : String(opt.id || ''),
        raw: opt.raw,
        struct: opt.struct,
        comment: opt.comment,
        isPrimary: opt.kind === 'primary',
    };
}

function formatCity(struct: AddressStruct | null): string {
    if (!struct) return '';
    const cityType = toTrimmedString((struct as unknown as Record<string, unknown>).cityType);
    const city = toTrimmedString((struct as unknown as Record<string, unknown>).city);
    return `${cityType ? `${cityType}. ` : ''}${city}`.trim();
}

function formatStreet(struct: AddressStruct | null): string {
    if (!struct) return '';
    const stType = toTrimmedString((struct as unknown as Record<string, unknown>).streetType);
    const st = toTrimmedString((struct as unknown as Record<string, unknown>).street);
    return `${stType ? `${stType} ` : ''}${st}`.trim();
}

function pickStructField(struct: AddressStruct | null, key: keyof AddressStruct): string {
    if (!struct) return '';
    const v = (struct as unknown as Record<string, unknown>)[key as unknown as string];
    const s = toTrimmedString(v);
    if (!s) return '';
    if (key === 'house') return `дом ${s}`;
    if (key === 'block') return `корп./стр. ${s}`;
    if (key === 'flat') return `кв. ${s}`;
    return s;
}

export const DeliverySection = React.forwardRef<HTMLElement, Props>(
    (
        {
            authUser,
            isAuthenticated,
            value,
            errors,
            onChange,
            shippingMethod,
            setShippingMethod,
            shippingCost,
            shippingError,
            shippingStatusText,
            isFreeDeliveryEligible,
            isFreeDeliveryZone,
            freeDeliveryMinTotal,
            goodsPayable,
            onSelectedAddressChange,
            highlightPickupNote = false,
        },
        ref,
    ) => {
        useDispatch<AppDispatch>();
        const [addrPopupOpen, setAddrPopupOpen] = useState(false);
        const [addrPopupMode, setAddrPopupMode] = useState<'add' | 'edit'>('add');
        const [addrPopupTarget, setAddrPopupTarget] = useState<{
            id: string;
            raw: string;
            struct?: unknown | null;
            comment?: string;
            isPrimary: boolean;
        } | null>(null);

        const pendingAddSnapshotRef = useRef<{ prevAdditionalIds: Set<string>; prevPrimaryRawNorm: string } | null>(null);

        const options: AddressOption[] = useMemo(() => {
            const list: AddressOption[] = [];

            const primary = readPrimaryAddress(authUser);
            const primaryRawNorm = primary?.raw ? normalizeRawKey(primary.raw) : '';

            if (primary?.raw) {
                list.push({
                    key: 'primary',
                    kind: 'primary',
                    raw: primary.raw,
                    struct: primary.struct,
                    comment: primary.comment,
                });
            }

            const extras = readAdditionalAddresses(authUser)
                .filter((a) => !a.isDisabled)
                .filter((a) => normalizeRawKey(a.raw) !== primaryRawNorm);

            for (const a of extras) {
                list.push({
                    key: `additional:${a.id}`,
                    kind: 'additional',
                    id: a.id,
                    raw: a.raw,
                    struct: a.struct,
                    comment: a.comment,
                });
            }

            return list;
        }, [authUser]);

        const formAddressRaw = useMemo(() => (typeof value.address === 'string' ? value.address.trim() : ''), [value.address]);

        const initialSelectedKey = useMemo(() => {
            const wantedNorm = formAddressRaw ? normalizeRawKey(formAddressRaw) : '';
            if (wantedNorm) {
                const found = options.find((o) => normalizeRawKey(o.raw) === wantedNorm);
                if (found) return found.key;
            }
            const primary = options.find((o) => o.kind === 'primary');
            if (primary) return primary.key;
            return options[0]?.key || '';
        }, [formAddressRaw, options]);

        const [selectedKey, setSelectedKey] = useState<string>(initialSelectedKey);

        useEffect(() => {
            if (!selectedKey) {
                setSelectedKey(initialSelectedKey);
                return;
            }
            const stillExists = options.some((o) => o.key === selectedKey);
            if (stillExists) return;

            const wantedNorm = formAddressRaw ? normalizeRawKey(formAddressRaw) : '';
            if (wantedNorm) {
                const found = options.find((o) => normalizeRawKey(o.raw) === wantedNorm);
                if (found) {
                    setSelectedKey(found.key);
                    return;
                }
            }
            setSelectedKey(initialSelectedKey);
        }, [formAddressRaw, initialSelectedKey, options, selectedKey]);

        const selectedOption = useMemo(() => options.find((o) => o.key === selectedKey) ?? null, [options, selectedKey]);
        const hasNoAddresses = options.length === 0;

        useEffect(() => {
            if (!onSelectedAddressChange) return;
            if (!selectedOption) {
                onSelectedAddressChange(null);
                return;
            }
            onSelectedAddressChange({
                raw: selectedOption.raw,
                struct: selectedOption.struct,
                ...(selectedOption.comment ? { comment: selectedOption.comment } : {}),
            });
        }, [onSelectedAddressChange, selectedOption]);

        const fireAddressChange = useCallback(
            (nextRaw: string) => {
                const synthetic = {
                    target: { name: 'address', value: nextRaw },
                    currentTarget: { name: 'address', value: nextRaw },
                } as unknown as React.ChangeEvent<HTMLInputElement>;
                onChange(synthetic);
            },
            [onChange],
        );

        useEffect(() => {
            if (!isAuthenticated) return;
            if (shippingMethod === 'pickup') return;
            if (!selectedOption) return;

            const nextRaw = selectedOption.raw;
            if (nextRaw && nextRaw !== formAddressRaw) fireAddressChange(nextRaw);
        }, [fireAddressChange, formAddressRaw, isAuthenticated, selectedOption, shippingMethod]);

        const openAddAddress = useCallback(() => {
            if (!isAuthenticated) return;

            const prevIds = new Set(options.filter((o) => o.kind === 'additional' && o.id).map((o) => String(o.id)));
            const prevPrimary = options.find((o) => o.kind === 'primary')?.raw ?? '';
            pendingAddSnapshotRef.current = {
                prevAdditionalIds: prevIds,
                prevPrimaryRawNorm: normalizeRawKey(prevPrimary),
            };

            setAddrPopupMode('add');
            setAddrPopupTarget(null);
            setAddrPopupOpen(true);
        }, [isAuthenticated, options]);

        const openEditSelected = useCallback(() => {
            if (!isAuthenticated) return;
            if (!selectedOption) return;

            setAddrPopupMode('edit');
            setAddrPopupTarget(buildTargetForPopup(selectedOption));
            setAddrPopupOpen(true);
        }, [isAuthenticated, selectedOption]);

        const closeAddrPopup = useCallback(() => {
            setAddrPopupOpen(false);
        }, []);

        useEffect(() => {
            const snap = pendingAddSnapshotRef.current;
            if (!snap) return;

            const curPrimaryRaw = options.find((o) => o.kind === 'primary')?.raw ?? '';
            const curPrimaryNorm = normalizeRawKey(curPrimaryRaw);

            if (curPrimaryRaw && curPrimaryNorm !== snap.prevPrimaryRawNorm) {
                pendingAddSnapshotRef.current = null;
                setSelectedKey('primary');
                fireAddressChange(curPrimaryRaw);
                return;
            }

            const newAdditional = options.find((o) => o.kind === 'additional' && o.id && !snap.prevAdditionalIds.has(String(o.id)));
            if (!newAdditional) return;

            pendingAddSnapshotRef.current = null;
            setSelectedKey(newAdditional.key);
            fireAddressChange(newAdditional.raw);
        }, [fireAddressChange, options]);

        const handleSelectOption = useCallback(
            (opt: AddressOption) => {
                if (!isAuthenticated) return;
                setSelectedKey(opt.key);
                if (shippingMethod !== 'pickup') fireAddressChange(opt.raw);
            },
            [fireAddressChange, isAuthenticated, shippingMethod],
        );

        const structForView = useMemo(() => selectedOption?.struct ?? null, [selectedOption]);

        const city = useMemo(() => formatCity(structForView), [structForView]);
        const street = useMemo(() => formatStreet(structForView), [structForView]);
        const house = useMemo(() => pickStructField(structForView, 'house'), [structForView]);
        const block = useMemo(() => pickStructField(structForView, 'block'), [structForView]);
        const flat = useMemo(() => pickStructField(structForView, 'flat'), [structForView]);

        const showFreeDeliveryText = shippingMethod === 'dpd' && isFreeDeliveryZone && Number.isFinite(freeDeliveryMinTotal) && freeDeliveryMinTotal > 0;

        // const showFreeDeliveryHint =
        //     shippingMethod === 'dpd' &&
        //     isFreeDeliveryZone &&
        //     Number.isFinite(goodsPayable) &&
        //     Number.isFinite(freeDeliveryMinTotal) &&
        //     goodsPayable < freeDeliveryMinTotal;

        return (
            <section ref={ref} className="deliverySection" aria-label="Доставка">
                <header className="deliverySection__header">
                    <h3 className="deliverySection__title">Доставка</h3>
                </header>

                <div className="deliverySection__methods" role="radiogroup" aria-label="Способ получения">
                    <label className={`deliverySection__opt ${shippingMethod === 'dpd' ? 'deliverySection__opt--active' : ''}`}>
                        <input type="radio" name="shippingMethod" checked={shippingMethod === 'dpd'} onChange={() => setShippingMethod('dpd')} />
                        <span className="deliverySection__optMark" aria-hidden="true" />
                        <span className="deliverySection__optText">Курьером</span>
                    </label>

                    <label className={`deliverySection__opt ${shippingMethod === 'pickup' ? 'deliverySection__opt--active' : ''}`}>
                        <input type="radio" name="shippingMethod" checked={shippingMethod === 'pickup'} onChange={() => setShippingMethod('pickup')} />
                        <span className="deliverySection__optMark" aria-hidden="true" />
                        <span className="deliverySection__optText">Самовывоз (со склада в Москве)</span>
                    </label>
                </div>

                {shippingMethod === 'pickup' ? (
                    <div className="deliverySection__pickupNote-wrapper">
                        <div className="deliverySection__pickupNote deliverySection__pickupNote_info">
                            Самовывоз - детали выдачи будут уточнены менеджером после оформления заказа.
                        </div>
                        <div className="deliverySection__pickupNote">
                            <span className="deliverySection__pickupNote_label">Адрес:</span>Московская область, г.о. Химки, село Чашниково, Промышленная улица, с1к4
                        </div>
                        <div className="deliverySection__pickupNote">
                            <span className="deliverySection__pickupNote_label">Время работы:</span>Пн–Пт: 10:00–18:00
                        </div>
                    </div>
                ) : (
                    <div className="deliverySection__pickupNote-wrapper">
                        <div className={`deliverySection__pickupNote${highlightPickupNote ? ' deliverySection__pickupNote--highlight' : ''}`}>
                            Добавьте адрес или проверьте выбранный для доставки ваших покупок.
                        </div>
                    </div>
                )}

                {shippingMethod !== 'pickup' && (
                    <div className="deliverySection__address" aria-label="Адрес доставки">
                        {isAuthenticated ? (
                            <>
                                <div className="deliverySection__addrTabs" role="radiogroup" aria-label="Выбор адреса доставки">
                                    {options.map((opt) => {
                                        const checked = opt.key === selectedKey;
                                        const addressName = opt.struct ? opt.struct.city + ', ' + opt.struct.streetType + ' ' + opt.struct.street : opt.raw;
                                        return (
                                            <label
                                                key={opt.key}
                                                className={`deliverySection__addrTab${checked ? ' deliverySection__addrTab--active' : ''}`}
                                                title={opt.raw}
                                            >
                                                <input type="radio" name="deliveryAddressChoice" checked={checked} onChange={() => handleSelectOption(opt)} />
                                                <span className="deliverySection__addrMark" aria-hidden="true" />
                                                <span className="deliverySection__addrTabText">{addressName}</span>
                                            </label>
                                        );
                                    })}

                                    <button
                                        type="button"
                                        className={`deliverySection__addrTab deliverySection__addrTab--add${hasNoAddresses ? ' deliverySection__addrTab--addNew' : ''}`}
                                        onClick={openAddAddress}
                                    >
                                        <span className="deliverySection__addrMark deliverySection__addrMark--add" aria-hidden="true" />
                                        <span className="deliverySection__addrTabText">Добавить адрес</span>
                                    </button>
                                </div>

                                <div className="deliverySection__addrFields">
                                    <div className="deliverySection__addrRow2">
                                        <input
                                            className="deliverySection__addrInput"
                                            value={city}
                                            placeholder="Город*"
                                            readOnly
                                            onClick={openEditSelected}
                                            aria-label="Город"
                                        />
                                        <input
                                            className="deliverySection__addrInput"
                                            value={street}
                                            placeholder="Улица*"
                                            readOnly
                                            onClick={openEditSelected}
                                            aria-label="Улица"
                                        />
                                    </div>

                                    <div className="deliverySection__addrRow3">
                                        <input
                                            className="deliverySection__addrInput deliverySection__addrInput--small"
                                            value={house}
                                            placeholder="Дом*"
                                            readOnly
                                            onClick={openEditSelected}
                                            aria-label="Дом"
                                        />
                                        <input
                                            className="deliverySection__addrInput deliverySection__addrInput--small"
                                            value={block}
                                            placeholder="Корп./Стр.*"
                                            readOnly
                                            onClick={openEditSelected}
                                            aria-label="Корпус/строение"
                                        />
                                        <input
                                            className="deliverySection__addrInput deliverySection__addrInput--small"
                                            value={flat}
                                            placeholder="Квартира*"
                                            readOnly
                                            onClick={openEditSelected}
                                            aria-label="Квартира"
                                        />
                                    </div>

                                    {showFreeDeliveryText ? (
                                        <div className="deliverySection__addrNote">
                                            Доставка по вашему адресу бесплатная при покупке от {freeDeliveryMinTotal.toLocaleString('ru-RU')} руб.
                                        </div>
                                    ) : null}

                                    {/* {showFreeDeliveryHint && (
                                        <div className="deliverySection__hint">
                                            Для вашего адреса доступна бесплатная доставка при сумме заказа от {freeDeliveryMinTotal.toLocaleString('ru-RU')} ₽.
                                        </div>
                                    )} */}

                                    {errors.address ? <div className="deliverySection__addrError">{errors.address}</div> : null}
                                    {!errors.address && shippingError && shippingStatusText !== shippingError.trim() ? (
                                        <div className="deliverySection__addrError">{shippingError}</div>
                                    ) : null}
                                </div>

                                {/* важное: address остаётся значением формы (адрес для заказа), но не editable */}
                                <input type="hidden" name="address" value={formAddressRaw} />

                                <SidePopup open={addrPopupOpen} onClose={closeAddrPopup} className="deliverySection__addressSidePopup" showCloseButton={false}>
                                    <DeliveryAddressPopupContent
                                        user={authUser as unknown as UserProfile}
                                        mode={addrPopupMode}
                                        targetAddress={addrPopupTarget}
                                        onClose={closeAddrPopup}
                                    />
                                </SidePopup>
                            </>
                        ) : (
                            <div className="deliverySection__addrGuest">
                                <div className="deliverySection__addrGuestText">Авторизуйтесь, чтобы выбрать или добавить адрес доставки.</div>
                            </div>
                        )}
                    </div>
                )}

                {/* {shippingMethod === 'dpd' && (
                    <div className="deliverySection__calc">
                        <div className="deliverySection__row">
                            <span className="deliverySection__rowLabel">Стоимость доставки</span>
                            <span className="deliverySection__rowValue">
                                {isFreeDeliveryEligible ? (
                                    'Бесплатно'
                                ) : shippingStatusText ? (
                                    <em>{shippingStatusText}</em>
                                ) : shippingCost == null ? (
                                    <em>рассчитывается…</em>
                                ) : (
                                    `${shippingCost.toFixed(2)} ₽`
                                )}
                            </span>
                        </div>
                    </div>
                )} */}
            </section>
        );
    },
);
DeliverySection.displayName = 'DeliverySection';
